import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useState } from 'react'
import {
  Building2, Settings, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Search,
  LayoutDashboard, ClipboardCheck, AlertTriangle, Shield, Scale, Target,
  TrendingUp, FolderOpen, Crown, ShieldCheck, Database, Gavel, Repeat2,
  BrainCircuit, PanelsTopLeft, FolderKanban, Grid2x2, Calculator,
  Eye, Users, Lock,
  Dot, LayoutGrid,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { useContextNavSections } from '@/hooks/use-context-nav'
import { useJourneyStore } from '@/stores/useJourneyStore'
import { CONTEXTS } from '@/config/contexts'
import { useFeatureGate } from '@/hooks/useFeatureGate'
import type { NavItem } from '@/config/nav-registry'
import type { ContextId, ContextMeta } from '@/types/real-estate'

// ─── Icon map ────────────────────────────────────────────────────────────────
const ICON_MAP: Record<string, React.ElementType> = {
  LayoutDashboard, ClipboardCheck, AlertTriangle, Shield, Scale, Target,
  TrendingUp, FolderOpen, Crown, ShieldCheck, Database, Gavel, Repeat2,
  Search, BrainCircuit, Building2, PanelsTopLeft, FolderKanban, Grid2x2,
  Calculator, Eye, Users, Settings,
}
function getIcon(name: string) { return ICON_MAP[name] || LayoutDashboard }

// ─── Color map ───────────────────────────────────────────────────────────────
const COLOR_MAP: Record<string, string> = {
  sky: 'bg-sky-500', blue: 'bg-blue-500', emerald: 'bg-emerald-500',
  red: 'bg-red-500', purple: 'bg-purple-500', indigo: 'bg-indigo-500',
  amber: 'bg-amber-500', teal: 'bg-teal-500', orange: 'bg-orange-500',
  cyan: 'bg-cyan-500', pink: 'bg-pink-500', slate: 'bg-slate-500',
  violet: 'bg-violet-500', stone: 'bg-stone-500', green: 'bg-green-500',
  rose: 'bg-rose-500',
}

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
  /** #RESP-R3: in the laptop band (1024–1279px) the rail is force-collapsed and
   *  the expand/collapse toggle is hidden (there isn't room to expand to 280px). */
  forcedCollapsed?: boolean
}

// ─── Copilot ID helper ───────────────────────────────────────────────────────
/** Derive a copilot-id from a nav href: "/risk-pro/register" → "nav-risk-pro-register" */
function navCopilotId(href: string | undefined): string | undefined {
  if (!href) return undefined
  return 'nav-' + href.replace(/^\//, '').replace(/\//g, '-')
}

// ─── Gated child nav link ───────────────────────────────────────────────────
function GatedChildNavLink({ child }: { child: NavItem }) {
  const { isEnabled, isLoading } = useFeatureGate(child.featureId || '', 1)

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-md px-2 py-1 text-sm text-muted-foreground/50">
        <child.icon className="h-3.5 w-3.5 shrink-0 animate-pulse" />
        <span className="truncate">{child.title}</span>
      </div>
    )
  }

  if (!isEnabled) {
    return (
      <div
        className="flex items-center gap-2 rounded-md px-2 py-1 text-sm text-muted-foreground/40 cursor-not-allowed"
        title="Disponible avec un plan supérieur"
      >
        <child.icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{child.title}</span>
        <Lock className="h-3 w-3 shrink-0 ml-auto" />
      </div>
    )
  }

  return (
    <NavLink
      to={child.href!}
      data-copilot-id={navCopilotId(child.href)}
      className={({ isActive }) => cn(
        'flex items-center gap-2 rounded-md px-2 py-1 text-sm transition-colors',
        isActive ? 'bg-[hsl(var(--sidebar-active-bg))] text-[hsl(var(--sidebar-active-fg))]' : 'text-[hsl(var(--sidebar-fg))]/75 hover:bg-[hsl(var(--sidebar-hover-bg))] hover:text-[hsl(var(--sidebar-fg))]'
      )}
    >
      <child.icon className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{child.title}</span>
    </NavLink>
  )
}

// ─── NavItem renderer ────────────────────────────────────────────────────────
function NavItemComponent({
  item, collapsed, openSections, toggleSection,
}: {
  item: NavItem; collapsed: boolean
  openSections: Record<string, boolean>; toggleSection: (t: string) => void
}) {
  const location = useLocation()
  const isChildActive = item.children?.some(c => c.href && location.pathname.startsWith(c.href))

  if (item.children && item.children.length > 0) {
    const isOpen = openSections[item.title] || isChildActive
    return (
      <Collapsible open={!collapsed && isOpen}>
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <CollapsibleTrigger asChild>
                <button
                  onClick={() => !collapsed && toggleSection(item.title)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium',
                    'transition-colors hover:bg-[hsl(var(--sidebar-hover-bg))] hover:text-[hsl(var(--sidebar-fg))]',
                    isChildActive ? 'bg-[hsl(var(--sidebar-hover-bg))] text-[hsl(var(--sidebar-fg))]' : 'text-[hsl(var(--sidebar-fg))]/75',
                    collapsed && 'justify-center'
                  )}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left truncate">{item.title}</span>
                      <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', isOpen && 'rotate-180')} />
                    </>
                  )}
                </button>
              </CollapsibleTrigger>
            </TooltipTrigger>
            {collapsed && <TooltipContent side="right">{item.title}</TooltipContent>}
          </Tooltip>
        </TooltipProvider>
        <CollapsibleContent>
          <div className="ml-4 mt-0.5 flex flex-col gap-0.5 border-l pl-2">
            {item.children.map((child) => (
              child.featureId ? (
                <GatedChildNavLink key={child.href} child={child} />
              ) : (
                <NavLink
                  key={child.href}
                  to={child.href!}
                  data-copilot-id={navCopilotId(child.href)}
                  className={({ isActive }) => cn(
                    'flex items-center gap-2 rounded-md px-2 py-1 text-sm transition-colors',
                    isActive ? 'bg-[hsl(var(--sidebar-active-bg))] text-[hsl(var(--sidebar-active-fg))]' : 'text-[hsl(var(--sidebar-fg))]/75 hover:bg-[hsl(var(--sidebar-hover-bg))] hover:text-[hsl(var(--sidebar-fg))]'
                  )}
                >
                  <child.icon className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{child.title}</span>
                </NavLink>
              )
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    )
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <NavLink
            to={item.href!}
            data-copilot-id={navCopilotId(item.href)}
            className={({ isActive }) => cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              isActive ? 'bg-[hsl(var(--sidebar-active-bg))] text-[hsl(var(--sidebar-active-fg))]' : 'text-[hsl(var(--sidebar-fg))]/75 hover:bg-[hsl(var(--sidebar-hover-bg))] hover:text-[hsl(var(--sidebar-fg))]',
              collapsed && 'justify-center'
            )}
          >
            <item.icon className="h-4 w-4 shrink-0" />
            {!collapsed && <span className="truncate">{item.title}</span>}
          </NavLink>
        </TooltipTrigger>
        {collapsed && <TooltipContent side="right">{item.title}</TooltipContent>}
      </Tooltip>
    </TooltipProvider>
  )
}

// ─── Inline Context Selector (Immobilia style) ──────────────────────────────
function InlineContextSelector({ collapsed }: { collapsed: boolean }) {
  const [open, setOpen] = useState(true)
  const activeContext = useJourneyStore((s) => s.activeContext)
  const setContext = useJourneyStore((s) => s.setContext)
  const navigate = useNavigate()

  const activeMeta = CONTEXTS.find(c => c.id === activeContext) || CONTEXTS[0]
  const ActiveIcon = getIcon(activeMeta.icon)

  const handleSelect = (ctx: ContextMeta) => {
    setContext(ctx.id)
    navigate(ctx.dashboardRoute)
  }

  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-1 py-2">
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={cn(
                'flex h-10 w-10 items-center justify-center rounded-xl',
                COLOR_MAP[activeMeta.color] || 'bg-primary',
              )}>
                <ActiveIcon className="h-5 w-5 text-white" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p className="font-semibold">{activeMeta.label}</p>
              <p className="text-xs text-muted-foreground">{activeMeta.description}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    )
  }

  return (
    <div className="px-3 py-2">
      {/* Active context header */}
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between mb-2"
      >
        <div className="flex items-center gap-2.5">
          <div className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
            COLOR_MAP[activeMeta.color] || 'bg-primary',
          )}>
            <ActiveIcon className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--sidebar-section-fg))]">
              Contexte actif
            </p>
            <p className="text-sm font-bold text-foreground">{activeMeta.label}</p>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="space-y-0.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--sidebar-section-fg))] mb-1">
            Choisir un contexte
          </p>

          {CONTEXTS.map(ctx => (
            <ContextButton key={ctx.id} ctx={ctx} isActive={activeContext === ctx.id} onSelect={handleSelect} />
          ))}
        </div>
      )}
    </div>
  )
}

function ContextButton({ ctx, isActive, onSelect }: {
  ctx: ContextMeta; isActive: boolean; onSelect: (c: ContextMeta) => void
}) {
  const Icon = getIcon(ctx.icon)
  const bgColor = COLOR_MAP[ctx.color] || 'bg-primary'

  return (
    <button
      onClick={() => onSelect(ctx)}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-all',
        isActive
          ? 'bg-primary/10 ring-1 ring-primary/20'
          : 'hover:bg-accent'
      )}
    >
      <div className={cn(
        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
        bgColor,
      )}>
        <Icon className="h-4 w-4 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <p className={cn(
          'text-sm font-semibold truncate',
          isActive ? 'text-primary' : 'text-foreground'
        )}>
          {ctx.label}
        </p>
        <p className="text-[11px] text-muted-foreground truncate">
          {ctx.description}
        </p>
      </div>
      {isActive && (
        <Dot className="h-6 w-6 text-primary shrink-0" />
      )}
    </button>
  )
}

// ─── Sidebar Search ──────────────────────────────────────────────────────────
function SidebarSearch({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative px-2 py-1.5">
      <Search className="absolute left-4.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
      <input
        type="text"
        placeholder="Rechercher..."
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full rounded-md border border-border/50 bg-accent/30 pl-8 pr-2 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
      />
    </div>
  )
}

function filterNavItems(items: NavItem[], query: string): NavItem[] {
  if (!query) return items
  const q = query.toLowerCase()
  return items.filter(item => {
    if ((item.title ?? '').toLowerCase().includes(q)) return true
    if (item.children?.some(c => (c.title ?? '').toLowerCase().includes(q))) return true
    return false
  }).map(item => {
    if (!item.children) return item
    const filtered = item.children.filter(c => (c.title ?? '').toLowerCase().includes(q) || (item.title ?? '').toLowerCase().includes(q))
    return { ...item, children: filtered.length > 0 ? filtered : item.children }
  })
}

// ─── Main Sidebar ────────────────────────────────────────────────────────────
export function Sidebar({ collapsed, onToggle, forcedCollapsed }: SidebarProps) {
  const { t } = useTranslation()
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  const [search, setSearch] = useState('')
  const { commonItems, contextItems, footerItems } = useContextNavSections()

  const toggleSection = (title: string) => {
    setOpenSections(prev => ({ ...prev, [title]: !prev[title] }))
  }

  // Filter items by search
  const filteredContext = filterNavItems(contextItems, search)
  const filteredCommon = filterNavItems(commonItems, search)
  const filteredFooter = filterNavItems(footerItems, search)

  return (
    <motion.aside
      initial={false}
      aria-label="Barre latérale"
      animate={{ width: collapsed ? 68 : 280 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      // #RESP-tablet: Sidebar (≈280px) + RightPanel (≈320px) both un-hid at `md`
      // (768) which crushed <main> to ~0px on tablet. #RESP-R3: the sidebar now
      // renders from `lg` (≥1024) as a FORCE-collapsed 68px rail in the laptop
      // band (1024–1279) — closing the "no sidebar on laptops" gap — and expands
      // to the user's toggle state only at `xl` (≥1280). Below `lg` the hamburger
      // MobileNavDrawer handles navigation.
      className="relative hidden lg:flex h-full flex-col border-r border-[hsl(var(--sidebar-border))] bg-[hsl(var(--sidebar-bg))] text-[hsl(var(--sidebar-fg))] shrink-0"
    >
      {/* Logo */}
      <div className="flex h-14 items-center border-b border-[hsl(var(--sidebar-border))] px-4 shrink-0">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--sidebar-active-bg))]">
            <Building2 className="h-5 w-5 text-[hsl(var(--sidebar-active-fg))]" />
          </div>
          {!collapsed && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="font-bold text-lg"
            >
              GovRisk
            </motion.span>
          )}
        </div>
      </div>

      {/* Context selector moved to TopBar (ContextGridSwitcher, grid view). */}

      {/* Search */}
      {!collapsed && (
        <div className="shrink-0 border-b border-[hsl(var(--sidebar-border))]">
          <SidebarSearch value={search} onChange={setSearch} />
        </div>
      )}

      {/* Navigation */}
      <ScrollArea className="flex-1 min-h-0">
        <nav aria-label="Navigation principale" className="flex flex-col gap-0.5 p-2">

          {/* Dashboard — always first */}
          <NavLink
            to="/dashboard"
            data-copilot-id="nav-dashboard"
            className={({ isActive }) => cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              isActive ? 'bg-[hsl(var(--sidebar-active-bg))] text-[hsl(var(--sidebar-active-fg))]' : 'text-[hsl(var(--sidebar-fg))]/75 hover:bg-[hsl(var(--sidebar-hover-bg))] hover:text-[hsl(var(--sidebar-fg))]',
              collapsed && 'justify-center'
            )}
          >
            <LayoutDashboard className="h-4 w-4 shrink-0" />
            {!collapsed && <span>{t('navigation.dashboard')}</span>}
          </NavLink>

          {/* Hub — feature card view for the active context */}
          <NavLink
            to="/hub"
            data-copilot-id="nav-hub"
            className={({ isActive }) => cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              isActive ? 'bg-[hsl(var(--sidebar-active-bg))] text-[hsl(var(--sidebar-active-fg))]' : 'text-[hsl(var(--sidebar-fg))]/75 hover:bg-[hsl(var(--sidebar-hover-bg))] hover:text-[hsl(var(--sidebar-fg))]',
              collapsed && 'justify-center'
            )}
          >
            <LayoutGrid className="h-4 w-4 shrink-0" />
            {!collapsed && <span>{t('navigation.hub')}</span>}
          </NavLink>

          {/* Context-specific items */}
          {filteredContext.length > 0 && (
            <>
              {!collapsed && (
                <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--sidebar-section-fg))]">
                  {t('navigation.features')}
                </p>
              )}
              {collapsed && <div className="my-1 border-t" />}
            </>
          )}

          {filteredContext.map((item) => (
            <NavItemComponent
              key={item.title}
              item={item}
              collapsed={collapsed}
              openSections={openSections}
              toggleSection={toggleSection}
            />
          ))}

          {/* Projet & Gestion */}
          {filteredCommon.length > 0 && (
            <>
              {!collapsed && (
                <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--sidebar-section-fg))]">
                  {t('navigation.projectManagement')}
                </p>
              )}
              {collapsed && <div className="my-1 border-t" />}
            </>
          )}

          {filteredCommon
            .filter(item => item.title !== 'Tableau de bord') // Already rendered above
            .map((item) => (
              <NavItemComponent
                key={item.title}
                item={item}
                collapsed={collapsed}
                openSections={openSections}
                toggleSection={toggleSection}
              />
            ))}

          {/* Outils & Admin */}
          {filteredFooter.length > 0 && (
            <>
              {!collapsed && (
                <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--sidebar-section-fg))]">
                  Outils & Admin
                </p>
              )}
              {collapsed && <div className="my-1 border-t" />}
            </>
          )}

          {filteredFooter.map((item) => (
            <NavItemComponent
              key={item.title}
              item={item}
              collapsed={collapsed}
              openSections={openSections}
              toggleSection={toggleSection}
            />
          ))}
        </nav>
      </ScrollArea>

      {/* Settings at bottom */}
      <div className="border-t border-[hsl(var(--sidebar-border))] p-2 shrink-0">
        <NavLink
          to="/settings"
          className={({ isActive }) => cn(
            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            isActive ? 'bg-[hsl(var(--sidebar-active-bg))] text-[hsl(var(--sidebar-active-fg))]' : 'text-[hsl(var(--sidebar-fg))]/75 hover:bg-[hsl(var(--sidebar-hover-bg))] hover:text-[hsl(var(--sidebar-fg))]',
            collapsed && 'justify-center'
          )}
        >
          <Settings className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Parametres</span>}
        </NavLink>
      </div>

      {/* Toggle — hidden in the forced-collapsed laptop band (no room to expand). */}
      {!forcedCollapsed && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          aria-label={collapsed ? 'Déplier la barre latérale' : 'Replier la barre latérale'}
          aria-expanded={!collapsed}
          className="absolute -right-3 top-16 z-10 hidden h-6 w-6 rounded-full border bg-background shadow-sm xl:flex"
        >
          {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
        </Button>
      )}
    </motion.aside>
  )
}
